import React, { useState } from "react";
import Title from "@atoms/Title/Title";
import Text from "@atoms/Text/Text";
import Icon from "@atoms/Icon/Icon";
import TitleGroup from "@atoms/TitleGroup/TitleGroup";
import Section from "@atoms/Section/Section";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Grid,
} from "@material-ui/core";
import "./ChapterUSP.scss";
import CommonActionButton from "./CommonActionButton";
import GenericIcon from "./GenericIcon";
import ResponsiveImg from "@atoms/ResponsiveImg";
import Img from "gatsby-image";
import { useIsMobile } from "../../hooks/useIsDevice";
import { useTracking } from "../../hooks";
import { AnalyticsEventType } from "../../context/TrackingProvider";

type Usp = {
	id: string;
	title: string;
	subtitle: string;
	icon: string;
	images: any[];
	content?: any;
};

interface Props {
	uspList: Usp[];
	cta: any;
	title?: string;
	queryParam?: string;
	enableTracking?: boolean;
}

const ChapterUSP: React.FC<Props> = ({ uspList, cta, title, queryParam, enableTracking }) => {
	const { track } = useTracking();
	const [tab, setTab] = useState<string | null>(
		uspList?.length > 0 ? uspList[0]?.id : null,
	);

	const handleTrackingInfo = (question: string) => {
		if (enableTracking) {
			track({
				eventType: AnalyticsEventType.GButtonClick,
				data: {
					sectionName: "chapter_usp",
					sourceUrl: window.location.pathname,
					question,
				},
			});
		}
	};
	
	return (
		<Section theme="light" container="large" className="ChapterUSP">
			{title && (
				<Section spacingBottom="xxs">
					<TitleGroup title={title} align="center" />
				</Section>
			)}

			<Grid container className="ChapterUSP--wrapper" spacing={4}>
				<Grid
					xs={12}
					sm={6}
					md={6}
					lg={6}
					item
					className="ChapterUSP--leftContent"
				>
					{uspList?.map((item: Usp) => {
						const desktopImage = item.images.find(
							(item: any) => item.media === "desktop",
						);
						const mobileImage = item.images.find(
							(item: any) => item.media === "mobile",
						);
						const isActive = tab === item.id;

						const isMobile = useIsMobile();
						return desktopImage?.src?.urlSharp?.childImageSharp?.fluid ||
							mobileImage?.src?.urlSharp?.childImageSharp?.fluid ? (
							<Img
								fluid={
									isMobile
										? mobileImage.src?.urlSharp.childImageSharp.fluid
										: desktopImage.src?.urlSharp.childImageSharp.fluid
								}
								style={{ position: "absolute" }}
								className={`ChapterUSP--image ${isActive ? "is-shown" : ""} `}
							/>
						) : (
							<ResponsiveImg
								desktopImage={desktopImage ? desktopImage.src?.url : undefined}
								mobileImage={mobileImage ? mobileImage.src?.url : undefined}
								style={{ position: "absolute" }}
								className={`ChapterUSP--image ${isActive ? "is-shown" : ""} `}
							/>
						);
					})}
				</Grid>

				<Grid
					xs={12}
					sm={6}
					md={6}
					lg={6}
					item
					className="ChapterUSP--rightContent"
				>
					{uspList?.map((item: Usp) => {
						const isActive = tab === item.id;
						const theme = isActive ? "highlighted" : "lighter";
						return (
							<Section spacingBottom="xs" key={`ChapterUSP_${item.id}`}>
								<Accordion expanded={isActive} onChange={() => {
									setTab(item.id);
									handleTrackingInfo(item.title)
								}}>
									<AccordionSummary>
										<Box display="flex" alignItems="center">
											<Box sx={{ mr: 1 }}>
												{
													<Icon
														icon={<GenericIcon icon={item.icon} />}
														theme={theme}
														size="m"
													/>
												}
											</Box>
											<Title noMargin size="l" theme={theme}>
												{item.title}
											</Title>
										</Box>
									</AccordionSummary>

									<AccordionDetails>
										<div>
											<Text>{item.subtitle}</Text>
											{item?.content && item?.content}
										</div>
									</AccordionDetails>
								</Accordion>
							</Section>
						);
					})}
					{cta && (
						<Section className="ChapterUSP--cta">
							<CommonActionButton
								cta={{
									...cta,
									ctaProps: {
										align: "left",
									},
								}}
								queryParam={queryParam}
								trackingInfo={enableTracking ? {
									buttonName: "chapter_usp_start_matching_button",
								} : undefined}
							/>
						</Section>
					)}
				</Grid>
			</Grid>
		</Section>
	);
};

export default ChapterUSP;
